/**
 * Copyright © 2026 Adnuntius AS.
 */
import {HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {AngularViewRefreshService} from '../../app/common/util/angular-view-refresh.service';

export interface AdnHttpRequest {
  method: string;
  url: string;
  params?: Record<string, any>;
  headers?: Record<string, any>;
  body?: any;
  responseType?: 'json' | 'text';
  cancel?: Promise<any>;
  hasErrorHandler?: boolean;
  ignore404?: boolean;
  /** Skip AuthHttpBufferService 401 buffering (auth endpoints, explicit opt-out). */
  ignoreAuthModule?: boolean;
}

export interface AdnHttpResponse {
  status: number;
  data: any;
  config: AdnHttpRequest;
  xhrStatus?: 'abort';
}

export interface AdnHttpCanceller {
  promise: Promise<any>;
  resolve: (reason?: any) => void;
}

export type AdnHttpProgressCallback = (event: {loaded: number; total?: number}) => void;

export function createAdnHttpCanceller(): AdnHttpCanceller {
  let resolve: (reason?: any) => void = () => undefined;
  const promise = new Promise<any>((promiseResolve) => {
    resolve = promiseResolve;
  });
  return {promise, resolve};
}

function toHttpParams(params?: Record<string, any>): HttpParams {
  let httpParams = new HttpParams();
  const serialise = (value: any): string => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };
  Object.keys(params || {}).forEach((key) => {
    const value = (params as Record<string, any>)[key];
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            httpParams = httpParams.append(key, serialise(item));
          }
        });
      } else {
        httpParams = httpParams.set(key, serialise(value));
      }
    }
  });
  return httpParams;
}

function toHttpHeaders(headers?: Record<string, any>): HttpHeaders {
  let httpHeaders = new HttpHeaders();
  Object.keys(headers || {}).forEach((key) => {
    const value = (headers as Record<string, any>)[key];
    if (value !== undefined && value !== null) {
      httpHeaders = httpHeaders.set(key, String(value));
    }
  });
  return httpHeaders;
}

/**
 * Pure Angular HTTP transport shared by resources upgraded off adnResource — no
 * $http/$resource/$q. Auth-header/context injection and API error handling stay
 * app-wide concerns, composed by callers (see create-adn-http-resource.ts) rather
 * than duplicated here.
 */
@Injectable({
  providedIn: 'root'
})
export class AdnHttpService {
  constructor(
    private readonly http: HttpClient,
    private readonly viewRefresh: AngularViewRefreshService
  ) {
  }

  request(config: AdnHttpRequest): Promise<AdnHttpResponse> {
    const requestOptions: any = {
      params: toHttpParams(config.params),
      headers: toHttpHeaders(config.headers),
      body: config.body,
      observe: 'response',
      responseType: config.responseType || 'json'
    };

    return new Promise<AdnHttpResponse>((resolve, reject) => {
      let settled = false;
      const subscription = this.http.request(config.method, config.url, requestOptions).subscribe(
        (response: any) => {
          settled = true;
          this.settle(() => {
            resolve({status: response.status, data: response.body, config});
          });
        },
        (err: HttpErrorResponse) => {
          settled = true;
          this.settle(() => {
            reject({status: err.status, data: err.error, config});
          });
        }
      );

      if (config.cancel) {
        config.cancel.then((reason: any) => {
          if (!settled) {
            settled = true;
            subscription.unsubscribe();
            this.settle(() => {
              reject({status: -1, data: reason, config, xhrStatus: 'abort'});
            });
          }
        }, () => undefined);
      }
    });
  }

  upload(config: AdnHttpRequest, onProgress?: AdnHttpProgressCallback): Promise<AdnHttpResponse> {
    const requestOptions: any = {
      params: toHttpParams(config.params),
      headers: toHttpHeaders(config.headers),
      body: config.body,
      observe: 'events',
      reportProgress: true,
      responseType: config.responseType || 'json'
    };

    return new Promise<AdnHttpResponse>((resolve, reject) => {
      this.http.request(config.method, config.url, requestOptions).subscribe(
        (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            onProgress?.({
              loaded: event.loaded,
              total: event.total
            });
          } else if (event.type === HttpEventType.Response) {
            this.settle(() => {
              resolve({status: event.status, data: event.body, config});
            });
          }
        },
        (err: HttpErrorResponse) => {
          this.settle(() => {
            reject({status: err.status, data: err.error, config});
          });
        }
      );
    });

  }

  /**
   * HttpClient's zoneless notify schedules a tick via setTimeout in the same
   * sync turn as the subscriber. Plain setTimeout(0) often runs FIRST, so
   * Promise.then mutations race that tick (NG0100). queueMicrotask → setTimeout
   * queues settle AFTER that notify timeout; viewRefresh runs after .then work.
   */
  private settle(fn: () => void): void {
    queueMicrotask(() => {
      setTimeout(() => {
        fn();
        queueMicrotask(() => this.viewRefresh.schedule());
      }, 0);
    });
  }
}
